import { createFileRoute } from "@tanstack/react-router";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { listJournal, addJournalEntry, deleteJournalEntry } from "@/lib/momentum.functions";
import { useBusiness } from "@/components/business-context";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useOfflineFn } from "@/lib/offline";

export const Route = createFileRoute("/_authenticated/app/journal")({
  head: () => ({
    meta: [
      { title: "Journal — The Founders App" },
      { name: "description", content: "Open-ended reflections for each business you're building." },
      { property: "og:title", content: "Journal — The Founders App" },
      { property: "og:description", content: "A free-write space, separate from the daily loop." },
    ],
  }),
  component: JournalPage,
});

function JournalPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const [body, setBody] = useState("");

  const fetchJournal = useServerFn(listJournal);
  const addEntry = useOfflineFn("addJournalEntry");
  const removeEntry = useOfflineFn("deleteJournalEntry");

  const query = useQuery({
    queryKey: ["journal", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchJournal({ data: { businessId } }),
  });

  const save = async () => {
    if (!body.trim()) return;
    try {
      await addEntry({ data: { businessId, body } });
      setBody("");
      void query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    }
  };

  if (!businessId) return null;

  return (
    <div className="space-y-8">
      <section className="vault-panel p-7">
        <p className="micro-label">Journal</p>
        <h2 className="mt-3 text-xl font-medium">Anything on your mind</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          No prompt, no structure. Write as much or as little as you like.
        </p>
        <Textarea
          className="mt-4 min-h-40 resize-none"
          placeholder="Today I noticed…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button className="sheen mt-6" onClick={() => void save()}>
          Save entry
        </Button>
      </section>

      <section className="space-y-4">
        {(query.data ?? []).map((entry) => (
          <article key={entry.id} className="soft-panel p-5">
            <div className="flex items-start justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                {new Date(entry.created_at).toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </p>
              <ConfirmDelete
                ariaLabel="Delete entry"
                itemLabel="this journal entry"
                onConfirm={async () => {
                  await removeEntry({ data: { id: entry.id } });
                  void query.refetch();
                }}
              >
                <Trash2 className="h-4 w-4" />
              </ConfirmDelete>
            </div>
            <p className="notranslate mt-3 leading-relaxed whitespace-pre-wrap">{entry.body}</p>
          </article>
        ))}
        {query.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing written yet. One honest paragraph is plenty.</p>
        )}
      </section>
    </div>
  );
}
